import FakeLink from "@/components/FakeLink/FakeLink";
import useUpload from "@/hooks/useUpload";
import { UploadProps } from "antd";
import { RcFile } from "antd/es/upload";
import Dragger from "antd/es/upload/Dragger";
import { AxiosError, isAxiosError } from "axios";
import { UploadRequestOption } from "rc-upload/lib/interface";
import { useValidateProductBom, useValidateProductTemplate } from "../../useProducts";
import { UploadProductActionStatus } from "./UploadProductAction";
import styles from "./UploadProductAction.module.scss";

export type OnSuccesfulFileSelection = ({
  fileName,
  s3FileName,
  uuid,
  newStatus,
}: {
  fileName: string;
  s3FileName: string;
  uuid: string;
  newStatus: UploadProductActionStatus;
}) => void;

export type OnUploadFails = (error: string | undefined) => void;

export default function UploadDragger({
  onSuccess: onFileUploadSuccess,
  onError: onFileUploadErrors,
  productId,
}: {
  onSuccess: OnSuccesfulFileSelection;
  onError: OnUploadFails;
  productId?: number;
}) {
  const { uploadFile } = useUpload();
  const validateMutation = useValidateProductTemplate();
  const validateBomMutation = useValidateProductBom();

  const handleUpload = async ({ onSuccess, onError, file }: UploadRequestOption) => {
    if (typeof file === "string" || !("name" in file)) throw Error("Uploaded file invalid.");

    const url = await uploadFile(file as RcFile);

    validateMutation
      .mutateAsync(url.key)
      .then(() => {
        validateBomMutation
          .mutateAsync({ fileName: url.key, productId })
          .then((result) => {
            onFileUploadSuccess({
              s3FileName: url.key,
              fileName: file.name,
              uuid: result.data.uuid,
              newStatus: "validating",
            });
            onSuccess?.("OK");
            onFileUploadErrors(undefined);
          })
          .catch((error: Error | AxiosError) => {
            if (isAxiosError(error) && error.response?.data.message) {
              onFileUploadErrors(
                `Initiating BOM validation failed: ${error.response?.data.message}. Try again or contact support.`
              );
            } else {
              onFileUploadErrors("Initiating BOM validation failed. Try again or contact support.");
            }
            onError?.({ status: 500, name: "Invalid format", message: "Invalid Format" });
          });
      })
      .catch((response: AxiosError<{ missing_columns: string[] }>) => {
        if (response.response?.data?.missing_columns) {
          onFileUploadErrors(
            `The following columns are missing in the file you uploaded: ${response.response.data.missing_columns.join(
              ", "
            )}.`
          );
        } else {
          onFileUploadErrors(
            "File validation failed for unknown reasons. Try again or contact support."
          );
        }
        onError?.({ status: 500, name: "Invalid format", message: "Invalid Format" });
      });
  };

  const draggerProps: UploadProps = {
    name: "file",
    multiple: false,
    style: { background: "#E6F7FF" },
    accept: ".csv,.xlsx,.xls",
    maxCount: 1,
    customRequest: (e) => handleUpload(e),
  };

  return (
    <Dragger {...draggerProps}>
      <span className={styles.title}>
        Drag file here or <FakeLink>click to browse</FakeLink>
      </span>
      <p className={styles.format}>
        You can upload <strong>.xlsx, .xlsx</strong> or <strong>.csv</strong> files.
      </p>
    </Dragger>
  );
}
